import React, { useRef, useEffect, useImperativeHandle, forwardRef } from "react";
import $ from "jquery";
import "daterangepicker"; // attaches to jQuery
import "daterangepicker/daterangepicker.css";
import moment from "moment";

// Shared From Date / To Date range picker (WES News Flyers tab, My Order List, ...).
// Rendered inside .catalog-dateSearch markup so the field styling and calendar icon
// are the same everywhere.
//
// The popup keeps its Apply / Cancel buttons (no autoApply):
//   Apply  - commits the range and calls onChange straight away
//   Cancel - drops the date filter (onChange(null)) and goes back to the default range
// A range picked but NOT applied is still available to the caller's Search button, via
// commitSelection() below.
//
// The input is deliberately UNCONTROLLED - daterangepicker drives the displayed text via
// $input.val(). Making it a controlled React input would put React and the jQuery plugin
// in a fight over the value.
const DISPLAY_FORMAT = "DD/MM/YYYY";
const VALUE_FORMAT = "YYYY-MM-DD";

const AUTOCLOSE_MS = Number(process.env.REACT_APP_DATEPICKER_AUTOCLOSE_MIN || 5) * 60 * 1000;

const defaultStart = () => moment().subtract(1, "month");
const defaultEnd = () => moment();

const rangeText = (start, end) => `${start.format(DISPLAY_FORMAT)} - ${end.format(DISPLAY_FORMAT)}`;

const toRange = (picker) => ({
  startDate: picker.startDate.format(VALUE_FORMAT),
  endDate: picker.endDate.format(VALUE_FORMAT),
  displayText: rangeText(picker.startDate, picker.endDate),
});

const parseRangeDate = (value) => {
  if (!value) return null;
  const parsed = moment(value, [VALUE_FORMAT, DISPLAY_FORMAT], true);
  return parsed.isValid() ? parsed : null;
};

export const buildPresetRanges = () => ({
  // Today: [moment(), moment()],
  // Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
  // "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
  // "This Week": [moment().startOf("week"), moment()],
  // "This Month": [moment().startOf("month"), moment()],
  "Last 7 Days": [moment().subtract(6, "days"), moment()],
  "Last 30 Days": [moment().subtract(29, "days"), moment()],
  "Last 60 Days": [moment().subtract(59, "days"), moment()],
  "Last 90 Days": [moment().subtract(89, "days"), moment()],
  "This Year": [moment().startOf("year"), moment()],
});

export const CustomDateRangePicker = forwardRef((props, ref) => {
  const {
    onChange,
    initialRange = null,
    showRanges = false,
    disabled = false,
    opens = "left",
    getDefaultStart = defaultStart,
  } = props;
  const inputRef = useRef(null);

  // The handlers below are bound once, so they would otherwise capture the onChange from
  // the first render. Callers read their own state inside it, so it must be the current one.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Whether the user has actually picked dates. The picker always holds a range (it
  // defaults to last month - today), so without this we could not tell "chosen" from
  // "never touched" and every search would silently be date-filtered.
  const hasSelectionRef = useRef(false);
  const autoCloseRef = useRef(null);

  useEffect(() => {
    const $input = $(inputRef.current);

    const restoredStart = initialRange ? parseRangeDate(initialRange.startDate) : null;
    const restoredEnd = initialRange ? parseRangeDate(initialRange.endDate) : null;
    const hasRestored = !!(restoredStart && restoredEnd);
    const openingStart = hasRestored ? restoredStart : getDefaultStart();
    const openingEnd = hasRestored ? restoredEnd : defaultEnd();

    $input.daterangepicker({
      opens: opens,
      autoUpdateInput: false,
      maxDate: new Date(),
      startDate: openingStart,
      endDate: openingEnd,
      showDropdowns: showRanges,
      alwaysShowCalendars: showRanges,
      showCustomRangeLabel: showRanges,
      ...(showRanges ? { ranges: buildPresetRanges() } : {}),
      locale: {
        format: DISPLAY_FORMAT,
        applyLabel: "Apply",
        cancelLabel: showRanges ? "Clear" : "Cancel",
        customRangeLabel: "Custom",
      },
    });

    const picker = $input.data("daterangepicker");

    if (showRanges) {
      picker.container.addClass("wes-dateRange-drop");
    }

    // Show the opening range without firing onChange, so nothing is filtered until
    // the user actually picks a range.
    hasSelectionRef.current = hasRestored;
    $input.val(rangeText(openingStart, openingEnd));

    // Clicking a day in the calendar counts as a selection even before Apply, so the
    // caller's Search button can pick it up.
    picker.container.on("click.wesDateRange", "td.available", () => {
      hasSelectionRef.current = true;
    });

    $input.on("show.daterangepicker", () => {
      clearTimeout(autoCloseRef.current);
      autoCloseRef.current = setTimeout(() => picker.hide(), AUTOCLOSE_MS);
    });

    $input.on("hide.daterangepicker", () => {
      clearTimeout(autoCloseRef.current);
    });

    $input.on("apply.daterangepicker", function (ev, p) {
      hasSelectionRef.current = true;
      $(this).val(rangeText(p.startDate, p.endDate));
      onChangeRef.current?.(toRange(p));
    });

    // Cancel drops the date filter rather than just closing the popup.
    $input.on("cancel.daterangepicker", function () {
      hasSelectionRef.current = false;
      picker.setStartDate(getDefaultStart());
      picker.setEndDate(defaultEnd());
      $(this).val(rangeText(getDefaultStart(), defaultEnd()));
      onChangeRef.current?.(null);
    });

    return () => {
      clearTimeout(autoCloseRef.current);
      if ($input.data("daterangepicker")) {
        $input.data("daterangepicker").container.off("click.wesDateRange");
        $input.data("daterangepicker").remove();
      }
    };
  }, []);

  useEffect(() => {
    const $input = $(inputRef.current);
    const picker = $input.data("daterangepicker");

    $input.prop("disabled", disabled);
    if (disabled && picker) {
      picker.hide();
    }
  }, [disabled]);

  useImperativeHandle(ref, () => ({
    // Used by the caller's Search button: returns the range currently showing in the
    // calendar, even if the user never pressed Apply, and syncs the input text to match.
    // Returns null when the user has not picked anything.
    commitSelection: () => {
      const $input = $(inputRef.current);
      const picker = $input.data("daterangepicker");
      if (!picker || !hasSelectionRef.current) return null;

      const range = toRange(picker);
      $input.val(range.displayText);
      return range;
    },
    reset: () => {
      const $input = $(inputRef.current);
      const picker = $input.data("daterangepicker");
      if (!picker) return;

      hasSelectionRef.current = false;
      picker.setStartDate(getDefaultStart());
      picker.setEndDate(defaultEnd());
      $input.val(rangeText(getDefaultStart(), defaultEnd()));
    },
  }));

  return (
    <div className={`catalog-dateSearch${disabled ? " is-disabled" : ""}`}>
      <input
        ref={inputRef}
        type="text"
        name="daterange"
        className="form-control"
        placeholder="From Date - To Date"
        autoComplete="off"
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
      />

      {/* Calendar Icon */}
      <span>
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path d="M6 1.5V3.75" stroke="#606060" strokeLinecap="round" />
          <path d="M12 1.5V3.75" stroke="#606060" strokeLinecap="round" />
          <path d="M2.625 6.81641H15.375" stroke="#606060" strokeLinecap="round" />
          <path
            d="M15.75 6.375V12.75C15.75 15 14.625 16.5 12 16.5H6C3.375 16.5 2.25 15 2.25 12.75V6.375C2.25 4.125 3.375 2.625 6 2.625H12C14.625 2.625 15.75 4.125 15.75 6.375Z"
            stroke="#606060"
          />
        </svg>
      </span>
    </div>
  );
});
